import {
  SkyBadge,
  SkyButton,
  SkyMessageBar,
  SkySkeleton,
  SkySkeletonItem,
} from "@skypointcloud/skypoint-ui";
import { useEffect, useMemo, useRef, useState } from "react";
import { Route, Switch, useLocation, useSearch } from "wouter";
import {
  runStatusListParam,
  runsQuerySchema,
  type RunEventExportFormat,
  type RunSummaryRow,
} from "@shared/types/runs";
import { CursorPager, NewerRowsNotice } from "@/components/paging/CursorPager";
import { CancelRunDialog } from "@/components/runs/CancelRunDialog";
import { RunFilters } from "@/components/runs/RunFilters";
import { RunsTable } from "@/components/runs/RunsTable";
import {
  RUN_KIND_LABELS,
  RUN_TYPE_LABELS,
  isTerminalRun,
  runStatusPresentation,
} from "@/components/runs/runFormatting";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { useApiErrorNotice } from "@/lib/feedback/useApiErrorNotice";
import { pagerRange, useCursorPager, useNextPage } from "@/hooks/use-cursor-pager";
import { useLiveRunRefresh } from "@/hooks/use-live-run-refresh";
import { usePermissions } from "@/hooks/use-permissions";
import {
  RUNS_DEFAULT_PAGE_SIZE,
  RUNS_PAGE_SIZES,
  useCancelRunById,
  useRunEventsExport,
  useRunsPage,
  usePollWhile,
  RUN_POLL_INTERVAL_MS,
  type RunFilterValues,
} from "@/hooks/use-runs";
import { useInstanceConnections, useSchedules } from "@/hooks/use-schedules";
import { failureMessage } from "@/lib/api";
import { DismissIcon } from "@/lib/icons";
import { absolutePath } from "@/lib/routes";
import RunDetailPage from "./runs/detail";
import { formatInstant, zoneCaption } from "@/lib/time/displayTime";
import { ZoneCaption } from "@/components/shell/ZoneCaption";
import { useDisplayTz } from "@/hooks/use-display-tz";

/**
 * Runs (UX-3, FR-56).
 *
 * Declared with `nest` in `App.tsx`, so this page owns `/runs` and
 * `/runs/:runId`. The list is cursor paginated (round 4, item 10a): Previous and Next over the
 * server's keyset cursor, 20 a page with 10/20/50 offered, and "1-20 of N" (`CursorPager`). Next
 * reads the page before it moves, so a failure leaves the page the reader was on in place rather
 * than a blank grid (NFR-8).
 */

function RunsListSkeleton() {
  return (
    <SkySkeleton>
      <div className="flex flex-col gap-2">
        {[0, 1, 2, 3, 4].map((row) => (
          <SkySkeletonItem key={row} size={44} />
        ))}
      </div>
    </SkySkeleton>
  );
}

/** The filters the list reads from its query string, in the order they are written back. */
const FILTER_KEYS = [
  "type",
  "status",
  "kind",
  "dataflowId",
  "scheduleId",
  "startedBy",
  "from",
  "to",
] as const;

/** One chip per filter, except the two dates: the picker sets them as one range, so one chip. */
type ChipKey = "type" | "status" | "kind" | "dataflowId" | "scheduleId" | "startedBy" | "dates";

/**
 * The filters in a query string (Session 10). Each value is checked with the route's own field
 * schema (`runsQuerySchema`), so a value the route would refuse with a 400 (an unknown status, an
 * id that is not a uuid, a date without a time and offset) is dropped rather than sent, and a
 * stale or hand-edited link still opens the list.
 */
export function filtersFromSearch(search: string): RunFilterValues {
  const params = new URLSearchParams(search);
  const fields = runsQuerySchema.shape;
  const filters: RunFilterValues = {};
  const status = fields.status.safeParse(params.get("status") ?? undefined);
  if (status.success && status.data?.length) filters.status = status.data;
  const kind = fields.kind.safeParse(params.get("kind") ?? undefined);
  if (kind.success && kind.data) filters.kind = kind.data;
  const type = fields.type.safeParse(params.get("type") ?? undefined);
  if (type.success && type.data) filters.type = type.data;
  for (const key of ["dataflowId", "scheduleId", "from", "to"] as const) {
    const parsed = fields[key].safeParse(params.get(key) ?? undefined);
    if (parsed.success && parsed.data) filters[key] = parsed.data;
  }
  const startedBy = fields.startedBy.safeParse(params.get("startedBy") ?? undefined);
  if (startedBy.success && startedBy.data) filters.startedBy = startedBy.data;
  return filters;
}

/** The dates chip: `Started: <from> to <to>`, or the one end that is set. */
function datesChipLabel(from: string | undefined, to: string | undefined, zone: string): string {
  const at = (iso: string | undefined) => formatInstant(iso, zone, "minutes");
  if (from && to) return `Started: ${at(from)} to ${at(to)}`;
  if (from) return `Started: from ${at(from)}`;
  return `Started: until ${at(to)}`;
}

/** The query string for a filter set, `""` when there is none. */
export function searchFromFilters(filters: RunFilterValues): string {
  const params = new URLSearchParams();
  for (const key of FILTER_KEYS) {
    const value = filters[key];
    if (key === "status") {
      if (filters.status?.length) params.set(key, runStatusListParam(filters.status));
    } else if (typeof value === "string" && value) {
      params.set(key, value);
    }
  }
  const query = params.toString();
  return query ? `?${query}` : "";
}

/** One removable chip per applied filter, so a filtered link says it is filtered. */
function filterChips(
  filters: RunFilterValues,
  names: {
    zone: string;
    connectionName: (id: string) => string;
    scheduleName: (id: string) => string;
  },
): Array<{ key: ChipKey; label: string }> {
  const chips: Array<{ key: ChipKey; label: string }> = [];
  if (filters.type) chips.push({ key: "type", label: `Type: ${RUN_TYPE_LABELS[filters.type]}` });
  if (filters.status?.length) {
    const labels = filters.status.map((status) => runStatusPresentation(status).label);
    chips.push({ key: "status", label: `Status: ${labels.join(" or ")}` });
  }
  if (filters.kind) chips.push({ key: "kind", label: `Trigger: ${RUN_KIND_LABELS[filters.kind]}` });
  if (filters.dataflowId) {
    chips.push({
      key: "dataflowId",
      label: `Connection: ${names.connectionName(filters.dataflowId)}`,
    });
  }
  if (filters.scheduleId) {
    chips.push({ key: "scheduleId", label: `Schedule: ${names.scheduleName(filters.scheduleId)}` });
  }
  if (filters.startedBy) chips.push({ key: "startedBy", label: "Started by: me" });
  if (filters.from || filters.to) {
    chips.push({ key: "dates", label: datesChipLabel(filters.from, filters.to, names.zone) });
  }
  return chips;
}

/** "12 runs shown (1-12 of 40), newest first, filtered by Status Failed". */
function runsCountLine(
  count: number,
  total: number | null,
  range: { start: number; end: number },
  appliedWords: string,
): string {
  // Round 4 (10a): where the page sits, when the server says how many there are.
  const where = total !== null && count > 0 ? ` (${range.start}-${range.end} of ${total})` : "";
  const filtered = appliedWords ? `, filtered by ${appliedWords}` : "";
  return `${count} run${count === 1 ? "" : "s"} shown${where}, newest first${filtered}`;
}

function runsEmptyMessage(failed: boolean, filtered: boolean): string {
  if (failed) return "Runs could not be loaded.";
  return filtered ? "No runs match these filters." : RUNS_EMPTY_MESSAGE;
}

/** A connection or schedule chip still waits for its name. */
function chipNamesPending(
  filters: RunFilterValues,
  connectionsLoading: boolean,
  schedulesLoading: boolean,
): boolean {
  return (
    (Boolean(filters.dataflowId) && connectionsLoading) ||
    (Boolean(filters.scheduleId) && schedulesLoading)
  );
}

/** The list's own read failed and there is nothing on screen to keep. */
function RunsLoadError({
  shown,
  error,
  onRetry,
}: {
  shown: boolean;
  error: unknown;
  onRetry: () => void;
}) {
  if (!shown) return null;
  return (
    <SkyMessageBar
      intent="error"
      layout="multiline"
      title="We could not load runs"
      message={`${failureMessage(error, "Try again.")} Your filters are unchanged.`}
      actions={
        <SkyButton size="small" onClick={onRetry}>
          Try again
        </SkyButton>
      }
      testId="runs-error"
    />
  );
}

/**
 * #2005: what the Runs list shows, for `useLiveRunRefresh`: the filters, the page, and whether the
 * rows are the previous filter's kept on screen (`keepPreviousRows`), each its own scope.
 */
function listScope(
  filterKey: string,
  pageSize: number,
  cursor: string | null,
  previousRows: boolean,
): string {
  return [filterKey, String(pageSize), cursor ?? "", previousRows ? "previous" : ""].join("|");
}

function RunsList() {
  const zone = useDisplayTz();
  const { canAny } = usePermissions();
  const notice = useNotice();
  const showApiError = useApiErrorNotice();
  const [, navigate] = useLocation();
  const search = useSearch();

  // The query string is the filter state (Session 10): a link from Overview or a connection
  // opens the list filtered, and Back returns to the filters that were applied. Memoised on
  // the string so `RunFilters` sees a new value only when the filters really change.
  const filters = useMemo(() => filtersFromSearch(search), [search]);
  const setFilters = (next: RunFilterValues): void => {
    // Replaced, not pushed: each quick filter is applied on change (Session 11, D8), and a pick
    // is not a page the user expects Back to step through.
    navigate(absolutePath(`/runs${searchFromFilters(next)}`), { replace: true });
  };
  // Removing a chip unmounts the button that had focus (and, with the last chip, the whole
  // group), so focus moves on: to the chip that took its place, else to the grid region.
  // Applied after the list re-renders with the new query string. A quick filter itself keeps
  // focus where it is (`RunFilters`).
  const focusAfterClear = useRef<ChipKey | "grid" | null>(null);
  useEffect(() => {
    const target = focusAfterClear.current;
    if (!target) return;
    focusAfterClear.current = null;
    const next =
      target === "grid"
        ? null
        : document.querySelector<HTMLElement>(`[data-testid="runs-filter-chip-clear-${target}"]`);
    (next ?? document.getElementById("runs-grid-region"))?.focus();
  }, [search]);
  // Session 11: the filters read the address on every render (the date picker included), so no
  // remount is needed to show what a chip's removal or a hand-edited link applied.
  const clearFilter = (key: ChipKey, remaining: readonly ChipKey[]): void => {
    const next = { ...filters };
    if (key === "dates") {
      delete next.from;
      delete next.to;
    } else {
      delete next[key];
    }
    const at = remaining.indexOf(key);
    focusAfterClear.current = remaining[at + 1] ?? remaining[at - 1] ?? "grid";
    setFilters(next);
  };
  // "Clear filters" unmounts itself (it shows only while a filter is applied), so focus moves
  // to the grid region, as after the last chip (ux-s10: focus never drops to the page body).
  const clearAllFilters = (): void => {
    focusAfterClear.current = "grid";
    setFilters({});
  };
  // Read in the click handler, so a ref: a second click lands before any re-render.
  const exporting = useRef(new Set<string>());
  const { exportEvents } = useRunEventsExport();

  // Round 4 (10a): a new filter set or page size starts at the first page; the stack's cursors
  // belong to the query they came from.
  const filterKey = searchFromFilters(filters);
  const pager = useCursorPager({
    pageSizes: RUNS_PAGE_SIZES,
    defaultPageSize: RUNS_DEFAULT_PAGE_SIZE,
    resetKey: filterKey,
  });
  const runs = useRunsPage(filters, pager.cursor, pager.pageSize, { keepPreviousRows: true });
  const connections = useInstanceConnections();
  const schedules = useSchedules();
  const cancelRun = useCancelRunById();

  const canCancel = canAny("runs.cancel");
  const canRetry = canAny("runs.retry");

  const connectionNames = useMemo(
    () =>
      new Map(
        (connections.data ?? []).map((connection) => [connection.id, connection.displayName]),
      ),
    [connections.data],
  );

  const rows = useMemo(() => runs.data?.rows ?? [], [runs.data]);
  // PR #1296 review: a row's Retry is off only while another run holds its connection
  // (`heldByRunId`, #1200), so the page on screen is re-read on the slow timer until none is.
  usePollWhile(
    rows.some((row) => Boolean(row.heldByRunId)),
    RUN_POLL_INTERVAL_MS,
    () => void runs.refetch(),
  );
  // #2005: the page holds the tab's live feed, so a run's start and terminal frames re-read this
  // page within the run-frame batch window (10 s at most), and the page is re-read on a slow timer
  // in case no frame comes: every 15 s while a row on screen moves and the socket is not live,
  // every 30 s otherwise. A filter or page change is its own scope, never a finish.
  useLiveRunRefresh({
    active: rows.some((row) => !isTerminalRun(row.status)),
    poll: () => void runs.refetch(),
    scope: listScope(filterKey, pager.pageSize, pager.cursor, runs.isPlaceholderData),
  });
  const total = runs.data?.total ?? null;
  const nextCursor = runs.data?.nextCursor ?? null;

  // Round 4 (10a): the live feed re-reads the page on screen. The first page takes new runs at
  // its top; a later page keeps its rows (the keyset) and only its total grows, so the rows that
  // arrived since the reader left the first page are counted, not shifted in under them.
  const next = useNextPage({
    pager,
    pageKey: `${filterKey}|${pager.pageSize}`,
    total,
    nextCursor,
    fetchPage: runs.fetchPage,
  });
  const newer = next.newer;
  const range = pagerRange({
    pageIndex: pager.pageIndex,
    pageSize: pager.pageSize,
    rowCount: rows.length,
    newer,
  });
  const goNext = next.goNext;

  const scheduleNames = useMemo(
    () => new Map((schedules.data ?? []).map((schedule) => [schedule.id, schedule.name])),
    [schedules.data],
  );

  const chips = filterChips(filters, {
    zone,
    connectionName: (id) =>
      connectionNames.get(id) ?? (connections.isLoading ? "Loading…" : "Unknown connection"),
    scheduleName: (id) =>
      scheduleNames.get(id) ?? (schedules.isLoading ? "Loading…" : "Unknown schedule"),
  });
  const chipKeys = chips.map((chip) => chip.key);

  const exportRun = async (row: RunSummaryRow, format: RunEventExportFormat): Promise<void> => {
    // One export at a time per run and format: a double click is one file and one audit row.
    const busyKey = `${row.runId}:${format}`;
    if (exporting.current.has(busyKey)) return;
    exporting.current.add(busyKey);
    try {
      const filename = await exportEvents(row.runId, format);
      notice.success(`Saved as ${filename}.`, { title: "Events exported" });
    } catch (error) {
      showApiError(error, "The events could not be exported.");
    } finally {
      exporting.current.delete(busyKey);
    }
  };

  const [cancelTarget, setCancelTarget] = useState<RunSummaryRow | null>(null);

  const cancel = async (row: RunSummaryRow): Promise<void> => {
    try {
      await cancelRun.mutateAsync(row.runId);
      notice.success(`Run ${row.runId} was cancelled.`);
    } catch (error) {
      showApiError(error, "The run could not be cancelled.");
    }
  };

  // The first load is the only one that blanks the grid. A failed next page never replaces the
  // page on screen (`goNext`), and a failed re-read keeps the rows it had.
  const isFirstLoad = runs.isLoading && rows.length === 0;

  // The polite count line speaks once per change, after the list settles (NFR-7): while the next
  // filter set loads, the previous rows stay (`keepPreviousRows`) and so does their count.
  // The line names the applied filters (ruling 14), so a pick that leaves the count the same is
  // still a new line and is still announced. It waits for a connection or schedule name, so a
  // "Loading…" chip label is never spoken and then corrected.
  const settled = !runs.isPlaceholderData && !runs.isFetching;
  const namesPending = chipNamesPending(filters, connections.isLoading, schedules.isLoading);
  // Adjusted during render rather than in an effect (react.dev, "You Might Not Need an Effect").
  const [settledSummary, setSettledSummary] = useState("Loading runs");
  const appliedWords = chips.map((chip) => chip.label.replace(": ", " ")).join(", ");
  // Round 4 (10a): where the page sits, when the server says how many there are.
  const countLine = runsCountLine(rows.length, total, range, appliedWords);
  if (settled && !namesPending && !isFirstLoad && countLine !== settledSummary) {
    setSettledSummary(countLine);
  }
  const resultSummary = isFirstLoad ? "Loading runs" : settledSummary;

  return (
    <>
      {/* Round 6, S5 (owner, 2026-10-01): Runs is the history screen, so it starts nothing. A
          run starts where its record is: Import now and Import with options on a connection,
          Run now on a schedule row, Run on Transformations. Retry and Cancel stay: they act on a
          run that exists. */}
      <RunsLoadError
        shown={runs.isError && rows.length === 0}
        error={runs.error}
        onRetry={() => void runs.refetch()}
      />

      {next.error ? (
        <div className="mb-3">
          <SkyMessageBar
            intent="warning"
            layout="multiline"
            title="The next page could not be loaded"
            message="The page you were reading is still here. Try again, or keep reading this one."
            actions={
              <SkyButton size="small" onClick={() => void goNext()}>
                Try again
              </SkyButton>
            }
            testId="runs-load-more-error"
          />
        </div>
      ) : null}

      {/* A failed re-read (Refresh, a live-feed re-read) with rows on screen keeps them and says
          so; the first-load bar above covers an empty grid, the warning above a failed Next. */}
      {runs.isError && rows.length > 0 && !next.error ? (
        <div className="mb-3">
          <SkyMessageBar
            intent="warning"
            layout="multiline"
            title="We could not refresh the runs"
            message={`${failureMessage(runs.error, "Try again.")} The runs on screen are still here.`}
            actions={
              <SkyButton size="small" onClick={() => void runs.refetch()}>
                Try again
              </SkyButton>
            }
            testId="runs-refresh-error"
          />
        </div>
      ) : null}

      {/* Focusable so removing the last chip lands a keyboard user on the answer rather than
          on the page body. The quick filters are the table's own bar (Session 11, D8). */}
      <section id="runs-grid-region" tabIndex={-1} aria-label={`Runs. ${zoneCaption(zone)}.`}>
        <RunFilters
          value={filters}
          onChange={setFilters}
          onClear={clearAllFilters}
          connections={connections.data ?? []}
          schedules={schedules.data ?? []}
          isFetching={runs.isFetching}
          resultSummary={resultSummary}
          onRefresh={() => void runs.refetch()}
        />
        {/* Below the bar (round-2 nit), in the bar's own strip so the bar, the chips and the grid
            stay one bordered block: the first pick adds this row under the bar, so the bar does
            not move under the pointer. */}
        {chips.length > 0 ? (
          <fieldset
            className="min-w-0 flex flex-wrap items-center gap-2 border-x border-border bg-muted px-3 pb-3"
            aria-label="Applied filters"
            data-testid="runs-filter-chips"
          >
            {chips.map((chip) => (
              <span
                key={chip.key}
                className="inline-flex items-center gap-1 rounded-md border border-border bg-muted py-0.5 pl-2 pr-0.5"
                data-testid={`runs-filter-chip-${chip.key}`}
              >
                <SkyBadge appearance="ghost" color="subtle">
                  {chip.label}
                </SkyBadge>
                <SkyButton
                  appearance="subtle"
                  size="small"
                  icon={DismissIcon}
                  aria-label={`Remove the filter ${chip.label}`}
                  onClick={() => clearFilter(chip.key, chipKeys)}
                  testId={`runs-filter-chip-clear-${chip.key}`}
                />
              </span>
            ))}
          </fieldset>
        ) : null}
        {newer > 0 ? (
          <div className="border-x border-border bg-muted px-3 pb-3">
            <NewerRowsNotice
              count={newer}
              one="run"
              other="runs"
              onBack={() => {
                void runs.invalidatePage(null);
                pager.first();
              }}
              testId="runs-newer"
            />
          </div>
        ) : null}
        {isFirstLoad ? (
          <RunsListSkeleton />
        ) : (
          <RunsTable
            rows={rows}
            isLoading={runs.isFetching}
            canCancel={canCancel}
            onCancel={setCancelTarget}
            connectionNames={connectionNames}
            scheduleNames={scheduleNames}
            canRetry={canRetry}
            onRetry={(row) => navigate(absolutePath(`/runs/${row.runId}?retry=1`))}
            onExport={(row, format) => void exportRun(row, format)}
            emptyMessage={runsEmptyMessage(runs.isError, chips.length > 0)}
          />
        )}
        {cancelTarget ? (
          <CancelRunDialog
            open
            onOpenChange={(open) => {
              if (!open) setCancelTarget(null);
            }}
            onConfirm={() => void cancel(cancelTarget)}
          />
        ) : null}
        {!isFirstLoad && (rows.length > 0 || !pager.isFirstPage) ? (
          <CursorPager
            testId="runs-pager"
            label="Runs pages"
            noun="runs"
            start={range.start}
            end={range.end}
            total={total}
            pageSize={pager.pageSize}
            pageSizes={pager.pageSizes}
            onPageSizeChange={pager.setPageSize}
            hasPrevious={!pager.isFirstPage}
            onPrevious={pager.previous}
            hasNext={nextCursor !== null}
            onNext={() => void goNext()}
            nextPending={next.pending}
            className="mt-3"
          />
        ) : null}
        {/* Round 6 walk W23: the zone is said only under rows that show a time. */}
        {rows.length > 0 ? <ZoneCaption testId="runs-zone-caption" className="mt-2" /> : null}
      </section>
    </>
  );
}

/**
 * The empty list (round 6, S5): the Runs page starts nothing, so it says where a run comes from.
 * "No runs yet" stays first: `e2e/walkthrough.spec.ts` looks for it.
 */
const RUNS_EMPTY_MESSAGE =
  "No runs yet. Runs appear here when a schedule fires or someone starts an import from Connections or Schedules, or a transformation run from Transformations.";

export default function RunsPage() {
  return (
    <Switch>
      <Route path="/:runId">
        {(params) => <RunDetailPage key={params.runId} runId={params.runId as string} />}
      </Route>
      <Route path="/">
        <RunsList />
      </Route>
    </Switch>
  );
}
